// ===============================================================
// ✅ callHelpers.js — LIVORA Calls Helpers
// ===============================================================

import { formatDateTimeEn } from "../../utils/digits";

export function toMillisSafe(value) {
  if (!value) return null;

  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();

  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

export function formatClock(ms) {
  if (!ms) return "";
  return formatDateTimeEn(ms, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateLabel(ms) {
  if (!ms) return "";
  return formatDateTimeEn(ms, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDuration(seconds) {
  const s = Number(seconds || 0);
  if (!Number.isFinite(s) || s <= 0) return "";

  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }

  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function getCallDirection(call, myUid) {
  if (call?.maleUid === myUid) return "outgoing";
  if (call?.femaleUid === myUid) return "incoming";
  return "unknown";
}

export function getCallOtherParty(call, myUid, t) {
  const direction = getCallDirection(call, myUid);
  const userFallback = t
    ? t("chat.user", { defaultValue: "مستخدم" })
    : "مستخدم";

  if (direction === "outgoing") {
    return {
      uid: call?.femaleUid || "",
      name:
        call?.femaleSnapshot?.displayName ||
        call?.otherUserName ||
        userFallback,
      gender: String(call?.otherUserGender || call?.femaleSnapshot?.gender || "female"),
      photo:
        call?.otherUserPhoto ||
        call?.femaleSnapshot?.photoURL ||
        "",
    };
  }

  if (direction === "incoming") {
    return {
      uid: call?.maleUid || "",
      name:
        call?.maleSnapshot?.displayName ||
        call?.otherUserName ||
        userFallback,
      gender: String(call?.otherUserGender || call?.maleSnapshot?.gender || "male"),
      photo:
        call?.otherUserPhoto ||
        call?.maleSnapshot?.photoURL ||
        "",
    };
  }

  return {
    uid: "",
    name: userFallback,
    gender: "",
    photo: "",
  };
}

export function getCallVisualState(call, myUid, t) {
  const direction = getCallDirection(call, myUid);
  const endedReason = String(call?.endedReason || "").trim();
  const durationSeconds = Number(call?.durationSeconds || 0);
  const startedAtMs = toMillisSafe(call?.startedAt);

  if (call?.status === "ongoing") {
    return {
      label: t("calls.ongoing", { defaultValue: "جارية الآن" }),
      className: "ongoing",
      missed: false,
    };
  }

  if (endedReason === "rejected") {
    return {
      label:
        direction === "incoming"
          ? t("calls.rejected_by_you", { defaultValue: "تم رفضها" })
          : t("calls.rejected", { defaultValue: "مرفوضة" }),
      className: "missed",
      missed: true,
    };
  }

  if (endedReason === "cancelled_before_answer") {
    return {
      label:
        direction === "incoming"
          ? t("calls.missed", { defaultValue: "مكالمة فائتة" })
          : t("calls.cancelled", { defaultValue: "تم الإلغاء قبل الرد" }),
      className: "missed",
      missed: true,
    };
  }

  if (startedAtMs || durationSeconds > 0 || endedReason === "ended_by_user") {
    return {
      label: t("calls.completed", { defaultValue: "مكالمة مكتملة" }),
      className: "done",
      missed: false,
    };
  }

  if (call?.status === "ringing") {
    return {
      label: t("calls.ringing", { defaultValue: "قيد الرنين" }),
      className: "ringing",
      missed: false,
    };
  }

  return {
    label: t("calls.unknown", { defaultValue: "غير معروفة" }),
    className: "unknown",
    missed: false,
  };
}